import React from 'react';

/**
 * MetricCard Component
 * 
 * A reusable card component for displaying journey metrics and other quantitative data.
 * Features customizable background colors and consistent typography for data visualization.
 * 
 * @description This component provides a standardized way to display key metrics with
 * an icon, label, and value. It's designed to be visually distinct with customizable
 * background colors to categorize different types of metrics (distance, cost, emissions, etc.).
 * 
 * @usage
 * ```tsx
 * // Journey summary metrics (primary use case)
 * <MetricCard
 *   icon="📏"
 *   label="Total Distance"
 *   value="340 km"
 *   backgroundColor="#fef3c7"
 * />
 * 
 * <MetricCard
 *   icon="💸"
 *   label="Total Cost"
 *   value="€150"
 *   backgroundColor="#dcfce7"
 * />
 * 
 * <MetricCard
 *   icon="🌱"
 *   label="CO₂ Emissions"
 *   value="12 kg of CO2e"
 *   backgroundColor="#dbeafe"
 * />
 * 
 * // Other quantitative data
 * <MetricCard
 *   icon="👥"
 *   label="Friends Visited"
 *   value="3"
 * />
 * ```
 * 
 * @usedIn
 * - Journey.tsx: Displays the three main journey metrics (Distance, Emissions, Cost)
 * - Potentially reusable for other dashboard-style metric displays across the platform
 * 
 * @designPrinciples
 * - Consistent card styling with proper spacing and borders
 * - Color-coded backgrounds for visual categorization of different metric types
 * - Clear typography hierarchy (small label, large value)
 * - Responsive design that works in grid layouts
 * - Uses CSS variables for theme compatibility
 * 
 * @colorScheme
 * The component uses specific background colors for different metric categories:
 * - Orange/cream (#fef3c7): Distance metrics
 * - Blue (#dbeafe): Environmental metrics (CO₂, emissions)
 * - Green (#dcfce7): Financial metrics (cost, budget)
 */

interface MetricCardProps {
  /** Icon to display in the header (emoji or icon string) */
  icon: string;
  /** Label text describing the metric */
  label: string;
  /** The metric value to display prominently */
  value: string;
  /** Background color for the card (hex, rgb, or CSS color name) */
  backgroundColor?: string;
  /** Text color for the value (defaults to CSS variable) */
  textColor?: string;
  /** Font size class for the value (defaults to text-lg) */
  fontSize?: string;
  /** Additional classes for the label row */
  labelClassName?: string;
  /** Additional classes for the value text */
  valueClassName?: string;
  /** Inline layout for mobile (value beside label) */
  inlineOnMobile?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  icon, 
  label, 
  value, 
  backgroundColor = '#f9fafb',
  textColor = 'var(--text)',
  fontSize = 'text-lg',
  labelClassName = '',
  valueClassName = '',
  inlineOnMobile = false
}) => {
  const containerClass = inlineOnMobile
    ? 'flex flex-row items-center justify-between sm:flex-col sm:items-stretch sm:justify-start'
    : '';
  const valueSpacingClass = inlineOnMobile ? 'mt-0 sm:mt-1 text-right sm:text-center' : 'mt-1 text-center';

  return (
    <div 
      className="rounded-md border border-[var(--border)] p-4"
      style={{ backgroundColor }}
    >
      <div className={containerClass}>
        <div className={`text-[var(--text-muted)] font-medium text-sm flex items-center gap-2 ${labelClassName}`}>
          <span>{icon}</span>
          <span>{label}</span>
        </div>
        <div 
          className={`${valueSpacingClass} font-semibold ${fontSize} ${valueClassName}`}
          style={{ color: textColor }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

export default MetricCard; 